import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Box,
  Avatar,
  Stack,
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import LocalShipping from '@mui/icons-material/LocalShipping';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import PhoneAndroid from '@mui/icons-material/PhoneAndroid';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';
import { useOrders } from '../context/OrderContext';
import DataService from '../services/dataService';
import type { Order } from '../services/types';
import { supabase } from '../services/supabaseClient';

const steps = ['Shipping Address', 'Payment Method', 'Review Order'];

type LogisticsMethod = 'self_delivery' | 'buyer_pickup' | 'farmershub_logistics' | 'third_party_logistics';

type PlatformLogisticsSettings = {
  enable_self_delivery: boolean;
  enable_buyer_pickup: boolean;
  enable_farmershub_logistics: boolean;
  enable_third_party_logistics: boolean;
  allow_product_level_overrides: boolean;
  default_self_delivery_fee: number;
  default_buyer_pickup_fee: number;
  default_farmershub_logistics_fee: number;
  default_third_party_logistics_fee: number;
  marketplace_commission_percent: number;
  logistics_commission_percent: number;
};

type ShopLogisticsSettings = {
  shop_id: string;
  enable_self_delivery: boolean;
  enable_buyer_pickup: boolean;
  enable_farmershub_logistics: boolean;
  enable_third_party_logistics: boolean;
  allow_product_level_overrides: boolean;
  pickup_location: string | null;
  pickup_instructions: string | null;
  logistics_notes: string | null;
};

type ProductLogisticsOverride = {
  product_id: string;
  shop_id: string;
  override_enabled: boolean;
  enable_self_delivery: boolean | null;
  enable_buyer_pickup: boolean | null;
  enable_farmershub_logistics: boolean | null;
  enable_third_party_logistics: boolean | null;
};

type CoverageRegion = {
  shop_id: string;
  method: LogisticsMethod;
  county: string | null;
  subcounty: string | null;
  radius_km: number | null;
};

type LogisticsOption = {
  method: LogisticsMethod;
  label: string;
  description: string;
  fee: number;
};

const LOGISTICS_METHOD_LABELS: Record<LogisticsMethod, string> = {
  self_delivery: 'Seller self delivery',
  buyer_pickup: 'Buyer pickup',
  farmershub_logistics: 'FarmersHub logistics',
  third_party_logistics: '3rd-party logistics',
};

const LEGACY_SHIPPING_FEE = 250;

const readFunctionErrorDetails = async (fnError: unknown) => {
  if (!fnError || typeof fnError !== 'object') return null;

  const directMessage = (fnError as { message?: unknown }).message;
  const context = (fnError as { context?: { json?: () => Promise<any>; text?: () => Promise<string> } }).context;
  const genericMessages = new Set([
    'Edge Function returned a non-2xx status code',
    'Failed to send a request to the Edge Function',
  ]);
  let payload: unknown = null;
  let text: string | null = null;
  let message: string | null = null;

  if (context) {
    try {
      if (typeof context.json === 'function') {
        payload = await context.json();
        if (typeof (payload as { error?: unknown })?.error === 'string' && (payload as { error: string }).error.trim()) {
          message = (payload as { error: string }).error.trim();
        } else if (
          typeof (payload as { message?: unknown })?.message === 'string' &&
          (payload as { message: string }).message.trim()
        ) {
          message = (payload as { message: string }).message.trim();
        }
      }
    } catch {
      // Fall through to text parsing when JSON is unavailable.
    }

    try {
      if (typeof context.text === 'function') {
        text = await context.text();
        if (!message && text.trim()) message = text.trim();
      }
    } catch {
      // Ignore and let the caller use the fallback message.
    }
  }

  if (!message && typeof directMessage === 'string' && directMessage.trim() && !genericMessages.has(directMessage.trim())) {
    message = directMessage.trim();
  }

  return {
    message,
    payload,
    text,
    directMessage: typeof directMessage === 'string' ? directMessage : null,
  };
};

const readFunctionErrorMessage = async (fnError: unknown) => {
  const details = await readFunctionErrorDetails(fnError);
  return details?.message ?? null;
};

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const { addresses, personalInfo } = useUser();
  const { addOrder, refreshOrders } = useOrders();

  const [activeStep, setActiveStep] = useState(0);
  const [shippingData, setShippingData] = useState({
    firstName: personalInfo.name?.split(' ')[0] || user?.user_metadata?.name?.split(' ')[0] || '',
    lastName: personalInfo.name?.split(' ').slice(1).join(' ') || user?.user_metadata?.name?.split(' ').slice(1).join(' ') || '',
    address1: '',
    address2: '',
    city: personalInfo.checkoutCity || 'Nairobi',
    state: personalInfo.checkoutState || '',
    zip: personalInfo.checkoutPostalCode || '',
    country: personalInfo.checkoutCountry || 'Kenya',
    phone: personalInfo.paymentPhone || personalInfo.phone || user?.phone || '',
    deliveryInstructions: personalInfo.deliveryInstructions || '',
  });
  const [paymentMethod, setPaymentMethod] = useState<'mpesa' | 'card'>(personalInfo.preferredPaymentMethod || 'mpesa');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showMpesaPrompt, setShowMpesaPrompt] = useState(false);
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [selectedLogisticsMethod, setSelectedLogisticsMethod] = useState<LogisticsMethod | ''>('');
  const [logisticsOptions, setLogisticsOptions] = useState<LogisticsOption[]>([]);
  const [logisticsLoading, setLogisticsLoading] = useState(false);
  const [logisticsWarning, setLogisticsWarning] = useState<string | null>(null);
  const [platformSettingsSnapshot, setPlatformSettingsSnapshot] = useState<PlatformLogisticsSettings>({
    enable_self_delivery: true,
    enable_buyer_pickup: true,
    enable_farmershub_logistics: true,
    enable_third_party_logistics: true,
    allow_product_level_overrides: true,
    default_self_delivery_fee: LEGACY_SHIPPING_FEE,
    default_buyer_pickup_fee: 0,
    default_farmershub_logistics_fee: LEGACY_SHIPPING_FEE,
    default_third_party_logistics_fee: LEGACY_SHIPPING_FEE,
    marketplace_commission_percent: 5,
    logistics_commission_percent: 5,
  });

  const subtotal = getCartTotal();
  const selectedLogisticsFee = logisticsOptions.find((option) => option.method === selectedLogisticsMethod)?.fee ?? LEGACY_SHIPPING_FEE;
  const marketplaceCommissionAmount = Number(((subtotal * platformSettingsSnapshot.marketplace_commission_percent) / 100).toFixed(2));
  const logisticsCommissionAmount = Number(((selectedLogisticsFee * platformSettingsSnapshot.logistics_commission_percent) / 100).toFixed(2));
  const totalAmount = subtotal + selectedLogisticsFee;

  const buyerRegionTokens = React.useMemo(
    () =>
      Array.from(
        new Set(
          [shippingData.state, shippingData.city, shippingData.country]
            .map((value) => String(value || '').trim().toLowerCase())
            .filter(Boolean)
        )
      ),
    [shippingData.city, shippingData.country, shippingData.state]
  );

  React.useEffect(() => {
    const loadEligibleLogisticsOptions = async () => {
      if (cartItems.length === 0) {
        setLogisticsOptions([]);
        setSelectedLogisticsMethod('');
        setLogisticsWarning(null);
        return;
      }

      setLogisticsLoading(true);
      setLogisticsWarning(null);

      try {
        const productIds = Array.from(new Set(cartItems.map((item) => String(item.id))));
        const { data: productRows, error: productError } = await supabase
          .from('products')
          .select('id, shop_id')
          .in('id', productIds);

        if (productError) throw productError;

        const productShopMap = new Map<string, string>();
        (productRows || []).forEach((row: any) => {
          if (row?.id && row?.shop_id) {
            productShopMap.set(String(row.id), String(row.shop_id));
          }
        });

        const normalizedItems = cartItems.map((item) => ({
          ...item,
          shopId: item.shop_id || productShopMap.get(String(item.id)) || null,
        }));

        const shopIds = Array.from(new Set(normalizedItems.map((item) => item.shopId).filter(Boolean))) as string[];

        if (shopIds.length === 0) {
          setLogisticsOptions([
            {
              method: 'farmershub_logistics',
              label: LOGISTICS_METHOD_LABELS.farmershub_logistics,
              description: 'Standard protected delivery is being used while shop logistics settings are configured.',
              fee: LEGACY_SHIPPING_FEE,
            },
          ]);
          setSelectedLogisticsMethod('farmershub_logistics');
          setLogisticsWarning('Seller logistics settings are not configured yet, so standard protected delivery is being used for this order.');
          return;
        }

        const [
          { data: platformSettingsData, error: platformSettingsError },
          { data: shopSettingsData, error: shopSettingsError },
          { data: coverageData, error: coverageError },
          { data: overrideData, error: overrideError },
        ] = await Promise.all([
          supabase
            .from('platform_logistics_settings')
            .select('enable_self_delivery, enable_buyer_pickup, enable_farmershub_logistics, enable_third_party_logistics, allow_product_level_overrides, default_self_delivery_fee, default_buyer_pickup_fee, default_farmershub_logistics_fee, default_third_party_logistics_fee, marketplace_commission_percent, logistics_commission_percent')
            .eq('settings_key', 'default')
            .maybeSingle(),
          supabase
            .from('shop_logistics_settings')
            .select('shop_id, enable_self_delivery, enable_buyer_pickup, enable_farmershub_logistics, enable_third_party_logistics, allow_product_level_overrides, pickup_location, pickup_instructions, logistics_notes')
            .in('shop_id', shopIds),
          supabase
            .from('shop_logistics_coverage_regions')
            .select('shop_id, method, county, subcounty, radius_km')
            .in('shop_id', shopIds)
            .eq('is_active', true),
          supabase
            .from('product_logistics_overrides')
            .select('product_id, shop_id, override_enabled, enable_self_delivery, enable_buyer_pickup, enable_farmershub_logistics, enable_third_party_logistics')
            .in('product_id', productIds),
        ]);

        if (platformSettingsError || shopSettingsError || coverageError || overrideError) {
          throw platformSettingsError || shopSettingsError || coverageError || overrideError;
        }

        const platformSettings: PlatformLogisticsSettings = {
          enable_self_delivery: Boolean(platformSettingsData?.enable_self_delivery ?? true),
          enable_buyer_pickup: Boolean(platformSettingsData?.enable_buyer_pickup ?? true),
          enable_farmershub_logistics: Boolean(platformSettingsData?.enable_farmershub_logistics ?? true),
          enable_third_party_logistics: Boolean(platformSettingsData?.enable_third_party_logistics ?? true),
          allow_product_level_overrides: Boolean(platformSettingsData?.allow_product_level_overrides ?? true),
          default_self_delivery_fee: Number(platformSettingsData?.default_self_delivery_fee ?? LEGACY_SHIPPING_FEE),
          default_buyer_pickup_fee: Number(platformSettingsData?.default_buyer_pickup_fee ?? 0),
          default_farmershub_logistics_fee: Number(platformSettingsData?.default_farmershub_logistics_fee ?? LEGACY_SHIPPING_FEE),
          default_third_party_logistics_fee: Number(platformSettingsData?.default_third_party_logistics_fee ?? LEGACY_SHIPPING_FEE),
          marketplace_commission_percent: Number(platformSettingsData?.marketplace_commission_percent ?? 5),
          logistics_commission_percent: Number(platformSettingsData?.logistics_commission_percent ?? 5),
        };
        setPlatformSettingsSnapshot(platformSettings);

        const shopSettingsMap = new Map<string, ShopLogisticsSettings>(
          ((shopSettingsData as ShopLogisticsSettings[]) || []).map((row) => [String(row.shop_id), row])
        );
        const coverageByShop = new Map<string, CoverageRegion[]>();
        ((coverageData as CoverageRegion[]) || []).forEach((row) => {
          const key = String(row.shop_id);
          coverageByShop.set(key, [...(coverageByShop.get(key) || []), row]);
        });
        const overrideMap = new Map<string, ProductLogisticsOverride>(
          ((overrideData as ProductLogisticsOverride[]) || []).map((row) => [String(row.product_id), row])
        );

        const methodDescriptions: Record<LogisticsMethod, string> = {
          self_delivery: 'The seller handles delivery directly within their active service regions.',
          buyer_pickup: 'You collect the order using the seller pickup details shown after checkout.',
          farmershub_logistics: 'Delivery is fulfilled using FarmersHub logistics where the route is active.',
          third_party_logistics: 'A seller-approved external logistics partner handles the delivery.',
        };
        const methodFees: Record<LogisticsMethod, number> = {
          self_delivery: platformSettings.default_self_delivery_fee,
          buyer_pickup: platformSettings.default_buyer_pickup_fee,
          farmershub_logistics: platformSettings.default_farmershub_logistics_fee,
          third_party_logistics: platformSettings.default_third_party_logistics_fee,
        };

        const multiShopCart = shopIds.length > 1;
        const globallyEnabledMethods = (Object.keys(LOGISTICS_METHOD_LABELS) as LogisticsMethod[]).filter((method) => {
          switch (method) {
            case 'self_delivery':
              return platformSettings.enable_self_delivery;
            case 'buyer_pickup':
              return platformSettings.enable_buyer_pickup;
            case 'farmershub_logistics':
              return platformSettings.enable_farmershub_logistics;
            case 'third_party_logistics':
              return platformSettings.enable_third_party_logistics;
          }
        });

        const perItemAllowedMethods = normalizedItems.map((item) => {
          const shopSettings = item.shopId ? shopSettingsMap.get(item.shopId) : null;
          const productOverride = overrideMap.get(String(item.id));
          const shopCoverage = item.shopId ? coverageByShop.get(item.shopId) || [] : [];

          const baseFlags: Record<LogisticsMethod, boolean> = {
            self_delivery: Boolean(shopSettings?.enable_self_delivery ?? true),
            buyer_pickup: Boolean(shopSettings?.enable_buyer_pickup ?? false),
            farmershub_logistics: Boolean(shopSettings?.enable_farmershub_logistics ?? false),
            third_party_logistics: Boolean(shopSettings?.enable_third_party_logistics ?? false),
          };

          if (
            platformSettings.allow_product_level_overrides &&
            Boolean(shopSettings?.allow_product_level_overrides ?? true) &&
            productOverride?.override_enabled
          ) {
            (Object.keys(baseFlags) as LogisticsMethod[]).forEach((method) => {
              const overrideValue = productOverride[
                method === 'self_delivery'
                  ? 'enable_self_delivery'
                  : method === 'buyer_pickup'
                    ? 'enable_buyer_pickup'
                    : method === 'farmershub_logistics'
                      ? 'enable_farmershub_logistics'
                      : 'enable_third_party_logistics'
              ];
              if (typeof overrideValue === 'boolean') {
                baseFlags[method] = overrideValue;
              }
            });
          }

          return globallyEnabledMethods.filter((method) => {
            if (!baseFlags[method]) return false;
            if (multiShopCart && (method === 'self_delivery' || method === 'buyer_pickup')) return false;

            if (method === 'buyer_pickup') return true;

            const matchingCoverage = shopCoverage.filter((row) => row.method === method);
            if (matchingCoverage.length === 0) return true;

            return matchingCoverage.some((row) =>
              [row.county, row.subcounty]
                .map((value) => String(value || '').trim().toLowerCase())
                .filter(Boolean)
                .some((token) => buyerRegionTokens.includes(token))
            );
          });
        });

        const sharedMethods = globallyEnabledMethods.filter((method) =>
          perItemAllowedMethods.every((methods) => methods.includes(method))
        );

        const nextOptions = sharedMethods.map((method) => ({
          method,
          label: LOGISTICS_METHOD_LABELS[method],
          description: methodDescriptions[method],
          fee: methodFees[method],
        }));

        setLogisticsOptions(nextOptions);
        setSelectedLogisticsMethod((current) =>
          current && nextOptions.some((option) => option.method === current) ? current : (nextOptions[0]?.method || '')
        );

        if (multiShopCart) {
          setLogisticsWarning(
            nextOptions.length > 0
              ? 'Your cart contains items from multiple shops, so pickup and seller self-delivery are hidden unless every seller can support a single shared logistics flow.'
              : 'No shared logistics method is currently available across all shops in your cart for the selected region.'
          );
        } else if (nextOptions.length === 0) {
          setLogisticsWarning('No seller-approved delivery method is currently available for the region in your shipping details.');
        }
      } catch (error: any) {
        console.error('Failed to load logistics options:', error);
        setLogisticsOptions([
          {
            method: 'farmershub_logistics',
            label: LOGISTICS_METHOD_LABELS.farmershub_logistics,
            description: 'Standard protected delivery is being used while logistics governance is finishing setup.',
            fee: LEGACY_SHIPPING_FEE,
          },
        ]);
        setSelectedLogisticsMethod('farmershub_logistics');
        setLogisticsWarning('Apply the logistics foundations migration to unlock seller delivery rules. Standard protected delivery is being used for now.');
      } finally {
        setLogisticsLoading(false);
      }
    };

    void loadEligibleLogisticsOptions();
  }, [buyerRegionTokens, cartItems]);

  // Pre-fill default address if available and form is empty
  React.useEffect(() => {
    const defaultAddr = addresses.find(a => a.isDefault);
    if (defaultAddr && !shippingData.address1) {
      setShippingData(prev => ({
        ...prev,
        address1: defaultAddr.address,
        // Simple heuristic for city/state if included in address string
        // otherwise default to Nairobi/Kenya
      }));
    }
  }, [addresses]);

  React.useEffect(() => {
    setShippingData(prev => ({
      ...prev,
      firstName: prev.firstName || personalInfo.name?.split(' ')[0] || user?.user_metadata?.name?.split(' ')[0] || '',
      lastName: prev.lastName || personalInfo.name?.split(' ').slice(1).join(' ') || user?.user_metadata?.name?.split(' ').slice(1).join(' ') || '',
      city: prev.city || personalInfo.checkoutCity || 'Nairobi',
      state: prev.state || personalInfo.checkoutState || '',
      zip: prev.zip || personalInfo.checkoutPostalCode || '',
      country: prev.country || personalInfo.checkoutCountry || 'Kenya',
      phone: prev.phone || personalInfo.paymentPhone || personalInfo.phone || user?.phone || '',
      deliveryInstructions: prev.deliveryInstructions || personalInfo.deliveryInstructions || '',
    }));
    setPaymentMethod(personalInfo.preferredPaymentMethod || 'mpesa');
  }, [personalInfo, user?.phone, user?.user_metadata?.name]);

  const handleSavedAddressSelect = (addressId: string) => {
    const selected = addresses.find(a => a.id === addressId);
    if (selected) {
      setShippingData(prev => ({
        ...prev,
        address1: selected.address,
      }));
    }
  };

  const applySavedCheckoutPreferences = () => {
    setShippingData(prev => ({
      ...prev,
      city: personalInfo.checkoutCity || prev.city || 'Nairobi',
      state: personalInfo.checkoutState || prev.state || '',
      zip: personalInfo.checkoutPostalCode || prev.zip || '',
      country: personalInfo.checkoutCountry || prev.country || 'Kenya',
      phone: personalInfo.paymentPhone || personalInfo.phone || prev.phone || '',
      deliveryInstructions: personalInfo.deliveryInstructions || prev.deliveryInstructions || '',
    }));
    setPaymentMethod(personalInfo.preferredPaymentMethod || paymentMethod);
    setCheckoutError(null);
  };

  const validateShippingStep = () => {
    const requiredFields = [
      ['firstName', shippingData.firstName],
      ['lastName', shippingData.lastName],
      ['address1', shippingData.address1],
      ['city', shippingData.city],
      ['zip', shippingData.zip],
      ['country', shippingData.country],
      ['phone', shippingData.phone],
    ];

    const missingField = requiredFields.find(([, value]) => !String(value || '').trim());
    if (missingField) {
      setCheckoutError('Please complete all required shipping details before continuing.');
      return false;
    }

    const normalizedPhone = shippingData.phone.replace(/\s+/g, '');
    if (!/^\+?\d{9,15}$/.test(normalizedPhone)) {
      setCheckoutError('Please enter a valid phone number for delivery and payment updates.');
      return false;
    }

    if (!selectedLogisticsMethod) {
      setCheckoutError('Choose an available delivery method before continuing.');
      return false;
    }

    return true;
  };

  const handleNext = () => {
    setCheckoutError(null);
    if (activeStep === 0 && !validateShippingStep()) {
      return;
    }

    if (activeStep === steps.length - 1) {
      void handlePlaceOrder();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handlePlaceOrder = async () => {
    if (cartItems.length === 0) {
      setCheckoutError('Your cart is empty. Add items before checking out.');
      return;
    }

    if (!validateShippingStep()) {
      return;
    }

    setIsProcessing(true);
    setCheckoutError(null);
    setPendingOrderId(null);
    // Create new order
    const shippingAddressStr = `${shippingData.address1}${shippingData.address2 ? ', ' + shippingData.address2 : ''}, ${shippingData.city}, ${shippingData.state ? shippingData.state + ', ' : ''}${shippingData.zip}, ${shippingData.country}`;
    
    try {
      let createdOrderId = `ORD-${Date.now()}`;

      if (user) {
        // Create in Database
        const dbOrder = await DataService.createOrder({
          userId: user.id,
          items: cartItems.map(item => ({
            productId: item.id.toString(),
            quantity: item.quantity,
            price: item.price,
            name: item.name
          })),
          totalAmount, // Including shipping
          shippingAddress: shippingData,
          paymentMethod: paymentMethod === 'mpesa' ? 'M-PESA' : 'Credit Card'
        });
        createdOrderId = dbOrder.id;

        const insertPayload: any = {
          user_id: user.id,
          order_id: createdOrderId,
          amount: totalAmount,
          phone_number: shippingData.phone || user.phone || 'unknown',
          provider: paymentMethod === 'mpesa' ? 'mpesa' : 'card',
          status: 'pending',
          metadata: {
            source: 'checkout',
            shipping_address: shippingData,
            logistics_method: selectedLogisticsMethod,
            logistics_fee: selectedLogisticsFee,
            marketplace_commission_percent: platformSettingsSnapshot.marketplace_commission_percent,
            marketplace_commission_amount: marketplaceCommissionAmount,
            logistics_commission_percent: platformSettingsSnapshot.logistics_commission_percent,
            logistics_commission_amount: logisticsCommissionAmount,
          },
          updated_at: new Date().toISOString(),
        };

        const { data: createdTx, error: insertError } = await supabase
          .from('payment_transactions')
          .insert(insertPayload)
          .select('id')
          .single();

        if (insertError) throw insertError;

        const { error: orderLogisticsError } = await supabase
          .from('orders')
          .update({
            logistics_method: selectedLogisticsMethod,
            logistics_fee: selectedLogisticsFee,
            merchandise_subtotal: subtotal,
            marketplace_commission_percent: platformSettingsSnapshot.marketplace_commission_percent,
            marketplace_commission_amount: marketplaceCommissionAmount,
            logistics_commission_percent: platformSettingsSnapshot.logistics_commission_percent,
            logistics_commission_amount: logisticsCommissionAmount,
            platform_fee_metadata: {
              merchandise_subtotal: subtotal,
              marketplace_commission_percent: platformSettingsSnapshot.marketplace_commission_percent,
              marketplace_commission_amount: marketplaceCommissionAmount,
              logistics_commission_percent: platformSettingsSnapshot.logistics_commission_percent,
              logistics_commission_amount: logisticsCommissionAmount,
              gross_total_amount: totalAmount,
            },
            logistics_metadata: {
              logistics_method: selectedLogisticsMethod,
              logistics_fee: selectedLogisticsFee,
              logistics_commission_percent: platformSettingsSnapshot.logistics_commission_percent,
              logistics_commission_amount: logisticsCommissionAmount,
              logistics_region: {
                city: shippingData.city,
                state: shippingData.state,
                country: shippingData.country,
              },
            },
            updated_at: new Date().toISOString(),
          })
          .eq('id', createdOrderId);

        if (orderLogisticsError) throw orderLogisticsError;

        let finalStatus: 'pending' | 'prompt_sent' | 'paid' | 'failed' = paymentMethod === 'mpesa' ? 'pending' : 'paid';
        let providerReference: string | null = null;
        let checkoutRequestId: string | null = null;
        let merchantRequestId: string | null = null;

        let paymentErrorMessage: string | null = null;

        if (paymentMethod === 'mpesa') {
          const { data: fnData, error: fnError } = await supabase.functions.invoke('mpesa-stk-push', {
            body: { amount: totalAmount, phoneNumber: shippingData.phone, orderId: createdOrderId, transactionId: createdTx.id }
          });

          if (fnError) {
            finalStatus = 'failed';
            const fnErrorDetails = await readFunctionErrorDetails(fnError);
            paymentErrorMessage = fnErrorDetails?.message ?? null;
          } else if (fnData && typeof fnData === 'object') {
            const rawStatus = (fnData as any).status;
            const rawRef = (fnData as any).provider_reference || (fnData as any).transaction_id;
            checkoutRequestId = (fnData as any).checkout_request_id ?? (fnData as any).CheckoutRequestID ?? null;
            merchantRequestId = (fnData as any).merchant_request_id ?? (fnData as any).MerchantRequestID ?? null;
            if (typeof (fnData as any).error === 'string' && (fnData as any).error.trim()) {
              paymentErrorMessage = (fnData as any).error.trim();
            }
            if (rawStatus === 'failed') finalStatus = 'failed';
            if (rawStatus === 'paid') finalStatus = 'paid';
            if (rawStatus === 'prompt_sent') finalStatus = 'prompt_sent';
            if (typeof rawRef === 'string') providerReference = rawRef;
          }
        }

        const { error: updateTxError } = await supabase
          .from('payment_transactions')
          .update({
            status: finalStatus,
            provider_reference: providerReference,
            checkout_request_id: checkoutRequestId,
            merchant_request_id: merchantRequestId,
            updated_at: new Date().toISOString(),
          })
          .eq('id', createdTx.id);

        if (updateTxError) throw updateTxError;

        if (finalStatus === 'failed') {
          await supabase
            .from('orders')
            .update({ payment_status: 'failed', updated_at: new Date().toISOString() })
            .eq('id', createdOrderId);
          throw new Error(paymentErrorMessage || 'M-Pesa payment initiation failed. Check the mpesa-stk-push Edge Function logs for the exact Daraja error.');
        }

        if (paymentMethod === 'mpesa' && finalStatus !== 'paid') {
          setPendingOrderId(createdOrderId);
          setShowMpesaPrompt(true);
          setIsProcessing(false);
          await refreshOrders();
          return;
        }

        if (finalStatus === 'paid') {
          await supabase
            .from('orders')
            .update({ payment_status: 'paid', updated_at: new Date().toISOString() })
            .eq('id', createdOrderId);
          await DataService.updateOrderStatus(createdOrderId, 'confirmed');
        }

        // Refresh orders to get the new one from DB
        await refreshOrders();
      } else {
        // Fallback for guest (local storage only via context)
        const newOrder: Order = {
          id: createdOrderId,
          date: new Date().toISOString(),
          status: 'processing',
          total: totalAmount,
          items: cartItems.map(item => ({
            id: item.id.toString(),
            productId: item.id.toString(),
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            image: item.image
          })),
          shippingAddress: shippingAddressStr,
          paymentMethod: paymentMethod === 'mpesa' ? 'M-PESA' : 'Credit Card',
        };
        addOrder(newOrder);
      }

      await clearCart();
      setIsProcessing(false);
      setShowMpesaPrompt(false);
      navigate('/order-success', { state: { orderId: createdOrderId } });
    } catch (error) {
      console.error('Order creation failed:', error);
      setIsProcessing(false);
      setShowMpesaPrompt(false);
      setCheckoutError(error instanceof Error ? error.message : 'Failed to place order. Please try again.');
    }
  };

  const handleShippingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShippingData({
      ...shippingData,
      [e.target.name]: e.target.value,
    });
  };

  const renderShippingForm = () => (
    <Grid container spacing={3}>
      {addresses.length > 0 && (
        <Grid item xs={12}>
           <Typography variant="subtitle2" gutterBottom>Saved Addresses</Typography>
           <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
             {addresses.map((addr) => (
               <Button 
                 key={addr.id} 
                 variant="outlined" 
                 size="small"
                 onClick={() => handleSavedAddressSelect(addr.id)}
                 startIcon={<LocalShipping />}
               >
                 {addr.type}: {addr.address.substring(0, 20)}...
               </Button>
             ))}
           </Box>
        </Grid>
      )}
      <Grid item xs={12}>
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={applySavedCheckoutPreferences}>
              Use Saved Details
            </Button>
          }
        >
          Use your saved checkout preferences from profile settings to speed up delivery and payment details.
        </Alert>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="firstName"
          name="firstName"
          label="First name"
          fullWidth
          value={shippingData.firstName}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="lastName"
          name="lastName"
          label="Last name"
          fullWidth
          value={shippingData.lastName}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          id="address1"
          name="address1"
          label="Address line 1"
          fullWidth
          value={shippingData.address1}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          id="address2"
          name="address2"
          label="Address line 2"
          fullWidth
          value={shippingData.address2}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="city"
          name="city"
          label="City"
          fullWidth
          value={shippingData.city}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          id="state"
          name="state"
          label="State/Province/Region"
          fullWidth
          value={shippingData.state}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="zip"
          name="zip"
          label="Zip / Postal code"
          fullWidth
          value={shippingData.zip}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="country"
          name="country"
          label="Country"
          fullWidth
          value={shippingData.country}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          id="phone"
          name="phone"
          label="Phone Number"
          fullWidth
          value={shippingData.phone}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          id="deliveryInstructions"
          name="deliveryInstructions"
          label="Delivery Instructions"
          fullWidth
          multiline
          rows={3}
          value={shippingData.deliveryInstructions}
          onChange={handleShippingChange}
          helperText="Optional notes for rider access, landmarks, or preferred delivery timing."
        />
      </Grid>
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">Delivery Method</Typography>
              <Typography variant="body2" color="text.secondary">
                Checkout only shows seller-approved logistics methods that are available for your current delivery region.
              </Typography>
            </Box>

            {logisticsLoading ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">Checking seller delivery rules...</Typography>
              </Stack>
            ) : logisticsOptions.length === 0 ? (
              <Alert severity="warning">
                No logistics method is currently available for this address. Update the delivery region or wait for the seller/admin to enable coverage.
              </Alert>
            ) : (
              <FormControl component="fieldset">
                <RadioGroup
                  value={selectedLogisticsMethod}
                  onChange={(e) => setSelectedLogisticsMethod(e.target.value as LogisticsMethod)}
                >
                  {logisticsOptions.map((option) => (
                    <FormControlLabel
                      key={option.method}
                      value={option.method}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {option.label} • KSh {option.fee.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}

            {logisticsWarning && (
              <Alert severity="info">
                {logisticsWarning}
              </Alert>
            )}
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderPaymentForm = () => (
    <FormControl component="fieldset">
      <FormLabel component="legend">Select Payment Method</FormLabel>
      <RadioGroup
        aria-label="payment-method"
        name="payment-method"
        value={paymentMethod}
        onChange={(e) => setPaymentMethod(e.target.value as 'mpesa' | 'card')}
      >
        <FormControlLabel
          value="mpesa"
          control={<Radio />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box 
                component="img" 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/15/M-PESA_LOGO-01.svg/1200px-M-PESA_LOGO-01.svg.png" 
                alt="M-Pesa" 
                sx={{ height: 35, width: 'auto', ml: 1 }} 
              />
            </Box>
          }
        />
        <FormControlLabel
          value="card"
          control={<Radio />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ mr: 1 }}>Credit/Debit Card</Typography>
              <Box 
                component="img" 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/2560px-Visa_Inc._logo.svg.png" 
                alt="Visa" 
                sx={{ height: 25, width: 'auto', ml: 1 }} 
              />
            </Box>
          }
        />
      </RadioGroup>

      {paymentMethod === 'mpesa' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          You will receive an M-Pesa prompt on your phone ({shippingData.phone || "provided number"}) after placing the order.
        </Alert>
      )}
    </FormControl>
  );

  const renderReviewOrder = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Order Summary</Typography>
      <Stack spacing={2}>
        {cartItems.map((item) => (
          <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar src={item.image} variant="rounded" sx={{ width: 50, height: 50, mr: 2 }} />
              <Box>
                <Typography variant="body1">{item.name}</Typography>
                <Typography variant="caption" color="text.secondary">Qty: {item.quantity}</Typography>
              </Box>
            </Box>
            <Typography variant="body1">KSh {(item.price * item.quantity).toLocaleString()}</Typography>
          </Box>
        ))}
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography>Subtotal</Typography>
        <Typography>KSh {getCartTotal().toLocaleString()}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography>Delivery</Typography>
        <Typography>KSh {selectedLogisticsFee.toLocaleString()}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Typography variant="h6">Total</Typography>
        <Typography variant="h6" color="primary">KSh {totalAmount.toLocaleString()}</Typography>
      </Box>

      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>Shipping To:</Typography>
          <Typography variant="body2">{shippingData.firstName} {shippingData.lastName}</Typography>
          <Typography variant="body2">{shippingData.address1}</Typography>
          <Typography variant="body2">{shippingData.city}, {shippingData.zip}</Typography>
          <Typography variant="body2">{shippingData.phone}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>Delivery Method:</Typography>
          <Typography variant="body2">{selectedLogisticsMethod ? LOGISTICS_METHOD_LABELS[selectedLogisticsMethod] : 'Not selected'}</Typography>
          <Typography variant="caption" color="text.secondary">
            Delivery fee: KSh {selectedLogisticsFee.toLocaleString()}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>Payment Method:</Typography>
          <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>{paymentMethod}</Typography>
        </Grid>
      </Grid>
    </Box>
  );

  if (cartItems.length === 0 && activeStep === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>Your cart is empty</Typography>
        <Button variant="contained" onClick={() => navigate('/marketplace')}>Go to Marketplace</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mb: 4, pt: 4 }}>
      <Paper variant="outlined" sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}>
        <Typography component="h1" variant="h4" align="center" gutterBottom>
          Checkout
        </Typography>
        <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 5 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <React.Fragment>
          {checkoutError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {checkoutError}
            </Alert>
          )}
          {activeStep === 0 && renderShippingForm()}
          {activeStep === 1 && renderPaymentForm()}
          {activeStep === 2 && renderReviewOrder()}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            {activeStep !== 0 && (
              <Button onClick={handleBack} sx={{ mr: 1 }} startIcon={<ArrowBack />}>
                Back
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isProcessing}
              endIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : (activeStep === steps.length - 1 ? <CheckCircle /> : <ArrowForward />)}
            >
              {activeStep === steps.length - 1 ? (isProcessing ? 'Processing...' : 'Place Order') : 'Next'}
            </Button>
          </Box>
        </React.Fragment>
      </Paper>

      <Dialog open={showMpesaPrompt} onClose={() => setShowMpesaPrompt(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress color="success" />
            <Typography variant="h6">M-Pesa Payment Pending</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 4 }}>
          <PhoneAndroid sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="body1" gutterBottom>
            An M-PESA payment request has been initiated for
          </Typography>
          <Typography variant="h6" color="primary" gutterBottom>
            {shippingData.phone || "your phone number"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sandbox note: you will not receive a real phone prompt on a live SIM. Use a Daraja sandbox test number (e.g. 254708374149) and complete the prompt in the Daraja simulator.
          </Typography>
          {pendingOrderId && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Order: {pendingOrderId}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Checkout;
